import React, { useState, useEffect } from 'react';
import { db, auth } from '../firebase';
import {
  collection, addDoc, onSnapshot, doc, updateDoc,
  deleteDoc, serverTimestamp, query, orderBy, arrayUnion, writeBatch
} from 'firebase/firestore';

export default function Notes({ user, setUnreadNotes }) {
  const [notes, setNotes]           = useState([]);
  const [form, setForm]             = useState({ assignTo: [], title: '', message: '', addToCalendar: false });
  const [saving, setSaving]         = useState(false);
  const [clearingAll, setClearingAll] = useState(false);
  const [expandedNote, setExpandedNote] = useState(null);
  const [replyTexts, setReplyTexts] = useState({});
  const [sendingReply, setSendingReply] = useState(null);

  useEffect(() => {
    const q = query(collection(db, 'notes'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(q, snap => {
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setNotes(data);
      const unread = data.filter(n => n.status === 'unread' || (!n.status && !n.completed)).length;
      setUnreadNotes(unread);
    });
    return unsub;
  }, [setUnreadNotes]);

  const toggleAssign = (name) =>
    setForm(f => ({
      ...f,
      assignTo: f.assignTo.includes(name) ? f.assignTo.filter(a => a !== name) : [...f.assignTo, name]
    }));

  const handleSave = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    await addDoc(collection(db, 'notes'), {
      assignTo:       form.assignTo,
      title:          form.title,
      message:        form.message,
      addToCalendar:  form.addToCalendar,
      status:         'unread',
      completed:      false,
      replies:        [],
      createdAt:      serverTimestamp(),
      createdBy:      user?.email || 'unknown',
    });
    setForm({ assignTo: [], title: '', message: '', addToCalendar: false });
    setSaving(false);
  };

  const markRead = async (id) =>
    updateDoc(doc(db, 'notes', id), { status: 'read' });

  const reopenNote = async (id) =>
    updateDoc(doc(db, 'notes', id), { status: 'unread' });

  const markCompleted = async (id) =>
    updateDoc(doc(db, 'notes', id), { completed: true, status: 'completed' });

  const deleteNote = async (id) => {
    await deleteDoc(doc(db, 'notes', id));
    if (expandedNote === id) setExpandedNote(null);
  };

  const clearAllNotes = async () => {
    if (!window.confirm(`Permanently delete all ${notes.length} note${notes.length !== 1 ? 's' : ''}? This cannot be undone.`)) return;
    setClearingAll(true);
    const batch = writeBatch(db);
    notes.forEach(n => batch.delete(doc(db, 'notes', n.id)));
    await batch.commit();
    setExpandedNote(null);
    setClearingAll(false);
  };

  const addReply = async (noteId) => {
    const text = (replyTexts[noteId] || '').trim();
    if (!text) return;
    setSendingReply(noteId);
    const u = auth.currentUser;
    await updateDoc(doc(db, 'notes', noteId), {
      replies: arrayUnion({
        text,
        author:         u?.email || 'unknown',
        authorInitials: (u?.displayName || u?.email || 'CO').slice(0, 2).toUpperCase(),
        createdAt:      new Date().toISOString(),
      }),
      status: 'unread',
    });
    setReplyTexts(r => ({ ...r, [noteId]: '' }));
    setSendingReply(null);
  };

  const fmtDate = (ts) => {
    if (!ts) return '';
    try {
      const d = ts.toDate ? ts.toDate() : new Date(ts);
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch { return ''; }
  };

  const fmtReplyDate = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ', ' +
             d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    } catch { return ''; }
  };

  const getNoteLabel = (note) => {
    const assignees = note.assignTo || [];
    if (assignees.length === 0) return 'General';
    return `To ${assignees.join(' & ')}`;
  };

  const statusStyles = {
    unread:    { bg: 'rgba(88,166,255,0.12)', color: 'var(--accent-blue)',  label: 'Unread'    },
    read:      { bg: 'rgba(88,166,255,0.08)', color: 'var(--text-muted)',   label: 'Read'      },
    completed: { bg: 'rgba(63,185,80,0.12)',  color: 'var(--accent-green)', label: 'Completed' },
  };

  return (
    <div className="notes-layout">
      {/* ── COMPOSE ── */}
      <div className="card" style={{ height: 'fit-content' }}>
        <div className="card-title">New Note</div>
        <div className="assign-row">
          {['Tiana', 'Phil'].map(name => (
            <label key={name} className="assign-checkbox">
              <input type="checkbox" checked={form.assignTo.includes(name)} onChange={() => toggleAssign(name)} />
              {name}
            </label>
          ))}
        </div>
        <div className="form-field">
          <label>Title</label>
          <input type="text" placeholder="Enter a title..." value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
        </div>
        <div className="form-field">
          <label>Message</label>
          <textarea placeholder="Enter the note message..." value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))} style={{ minHeight: 100 }} />
        </div>
        <label className="checkbox-row" style={{ marginBottom: 16 }}>
          <input type="checkbox" checked={form.addToCalendar} onChange={e => setForm(f => ({ ...f, addToCalendar: e.target.checked }))} />
          <span>Add to Calendar</span>
        </label>
        <button className="btn btn-primary" style={{ width: '100%' }} onClick={handleSave} disabled={saving || !form.title.trim()}>
          {saving ? 'Saving...' : 'Send Note'}
        </button>
      </div>

      {/* ── NOTE HISTORY ── */}
      <div className="card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div className="card-title" style={{ marginBottom: 0 }}>Note History</div>
          {notes.length > 0 && (
            <button
              className="btn btn-sm btn-danger"
              onClick={clearAllNotes}
              disabled={clearingAll}
              style={{ fontSize: 11 }}
            >
              {clearingAll ? 'Clearing…' : `🗑 Clear All (${notes.length})`}
            </button>
          )}
        </div>
        {notes.length === 0 ? (
          <div className="empty-state">No notes yet</div>
        ) : (
          notes.map(note => {
            const isExpanded = expandedNote === note.id;
            const st = statusStyles[note.status] || statusStyles.unread;
            const replies = note.replies || [];
            const replyCount = replies.length;

            return (
              <div key={note.id} style={{
                borderRadius: 8,
                border: `1px solid ${note.status === 'unread' && !note.completed ? 'var(--accent-blue)' : 'var(--border)'}`,
                marginBottom: 8,
                overflow: 'hidden',
                background: 'var(--bg-tertiary)',
              }}>
                {/* Note header row — always visible */}
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', cursor: 'pointer' }}
                  onClick={() => setExpandedNote(isExpanded ? null : note.id)}
                >
                  {/* Status dot */}
                  {note.status === 'unread' && !note.completed && (
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--accent-blue)', flexShrink: 0 }} />
                  )}

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: st.color, background: st.bg, padding: '2px 7px', borderRadius: 4 }}>
                        {getNoteLabel(note)}
                      </span>
                      <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {note.title}
                      </span>
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2, display: 'flex', gap: 10 }}>
                      <span>{fmtDate(note.createdAt)}</span>
                      {replyCount > 0 && <span style={{ color: 'var(--accent-blue)' }}>💬 {replyCount} repl{replyCount !== 1 ? 'ies' : 'y'}</span>}
                    </div>
                  </div>

                  {/* Actions */}
                  <div style={{ display: 'flex', gap: 6, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                    {!note.completed && note.status !== 'read' && (
                      <button className="btn btn-sm" style={{ background: 'var(--accent-blue-bg)', color: 'var(--accent-blue)', border: 'none' }} onClick={() => markRead(note.id)}>
                        Mark Read
                      </button>
                    )}
                    {!note.completed && note.status === 'read' && (
                      <button className="btn btn-sm btn-secondary" onClick={() => reopenNote(note.id)}>
                        Reopen
                      </button>
                    )}
                    {!note.completed && (
                      <button className="btn btn-sm btn-success" onClick={() => markCompleted(note.id)}>✓</button>
                    )}
                    {note.completed && (
                      <span style={{ fontSize: 11, color: 'var(--accent-green)', fontWeight: 600, padding: '2px 8px' }}>Completed</span>
                    )}
                    <button className="btn btn-sm btn-danger" onClick={() => deleteNote(note.id)}>🗑</button>
                    <span style={{ color: 'var(--text-muted)', fontSize: 13, alignSelf: 'center' }}>{isExpanded ? '▲' : '▼'}</span>
                  </div>
                </div>

                {/* Expanded: message + replies + reply input */}
                {isExpanded && (
                  <div style={{ borderTop: '1px solid var(--border)', padding: '14px 16px', background: 'var(--bg-secondary)' }}>
                    {/* Original message */}
                    {note.message && (
                      <div style={{ fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.6, marginBottom: 16, padding: '10px 12px', background: 'var(--bg-tertiary)', borderRadius: 6, borderLeft: '3px solid var(--accent-blue)' }}>
                        {note.message}
                      </div>
                    )}

                    {/* Replies */}
                    {replies.length > 0 && (
                      <div style={{ marginBottom: 14 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
                          Replies
                        </div>
                        {replies.map((r, i) => (
                          <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
                            <div style={{ width: 30, height: 30, borderRadius: '50%', background: 'var(--bg-tertiary)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: 'var(--accent-blue)', flexShrink: 0 }}>
                              {r.authorInitials || '??'}
                            </div>
                            <div style={{ flex: 1, background: 'var(--bg-tertiary)', borderRadius: 6, padding: '8px 12px' }}>
                              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                                <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)' }}>{r.author || 'Unknown'}</span>
                                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{fmtReplyDate(r.createdAt)}</span>
                              </div>
                              <div style={{ fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.5 }}>{r.text}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Reply input */}
                    {!note.completed && (
                      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                        <textarea
                          placeholder="Write a reply..."
                          value={replyTexts[note.id] || ''}
                          onChange={e => setReplyTexts(r => ({ ...r, [note.id]: e.target.value }))}
                          onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) addReply(note.id); }}
                          style={{ flex: 1, minHeight: 60, resize: 'vertical' }}
                        />
                        <button
                          className="btn btn-primary btn-sm"
                          style={{ flexShrink: 0, marginBottom: 2 }}
                          onClick={() => addReply(note.id)}
                          disabled={sendingReply === note.id || !(replyTexts[note.id] || '').trim()}
                        >
                          {sendingReply === note.id ? '...' : 'Reply'}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
